import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Container, Box, Button, Typography, Paper, Stack, CircularProgress, Divider, TextField, Alert, Link, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { captureException } from '../utils/captureException';

const Login: React.FC = () => {
  const { user, loading, signInWithGoogle, signInWithMicrosoft, signInWithEmail, signUpWithEmail, resetPassword } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const parseErrorMessage = (error: any): string => {
    // Check if the error message contains specific domain error messages from Firebase function
    if (error.message) {
      if (error.message.includes('not authorized') || error.message.includes('domain')) {
        return t('auth.domainNotAuthorized');
      }
      if (error.message.includes('Registration failed')) {
        return error.message; // Use the specific message from the Firebase function
      }
    }

    // Handle standard Firebase Auth error codes
    switch (error.code) {
      case 'auth/user-not-found':
        return t('auth.userNotFound');
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return t('auth.invalidCredentials');
      case 'auth/email-already-in-use':
        return t('auth.emailAlreadyExists');
      case 'auth/weak-password':
        return t('auth.weakPassword');
      case 'auth/invalid-email':
        return t('auth.invalidEmail');
      case 'auth/permission-denied':
        return t('auth.domainNotAuthorized');
      case 'auth/internal-error':
        return t('auth.serverError');
      default:
        return isSignUp ? t('auth.accountCreationFailed') : t('auth.signInError');
    }
  };

  const parseResetPasswordError = (error: any): string => {
    // Handle password reset specific error codes
    switch (error.code) {
      case 'auth/invalid-email':
        return t('auth.invalidEmail');
      case 'auth/user-not-found':
        // For security, Firebase doesn't reveal if user exists, but if it does throw this error, handle it
        return t('auth.resetPasswordError');
      case 'auth/too-many-requests':
        return t('auth.resetPasswordError');
      case 'auth/network-request-failed':
        return t('auth.resetPasswordError');
      default:
        return t('auth.resetPasswordError');
    }
  };

  const handleResetPassword = async () => {
    setResetError('');
    setResetSuccess(false);
    
    if (!resetEmail) {
      setResetError(t('auth.invalidEmail'));
      return;
    }
    
    setIsResetting(true);
    try {
      await resetPassword(resetEmail);
      setResetSuccess(true);
      setTimeout(() => {
        setResetDialogOpen(false);
        setResetEmail('');
        setResetSuccess(false);
      }, 3000);
    } catch (error: any) {
      captureException(error, { tags: { feature: 'login' }, extra: { action: 'resetPassword' } });
      console.error('Failed to reset password:', error);
      setResetError(parseResetPasswordError(error));
    } finally {
      setIsResetting(false);
    }
  };

  const handleOpenResetDialog = () => {
    setResetDialogOpen(true);
    setResetEmail(email); // Pre-fill with the email from the form if available
    setResetError('');
    setResetSuccess(false);
  };

  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setResetEmail('');
    setResetError('');
    setResetSuccess(false);
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    console.log('handleEmailSignIn!!!!!!!!');
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    
    // Validate password confirmation for sign-up
    if (isSignUp && password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      setIsSubmitting(false);
      return;
    }
    
    try {
      if (isSignUp) {
        const user = await signUpWithEmail(email, password);
        // Call backend API to create user profile with name
        const idToken = await user.user.getIdToken();
        await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/user`, {
          username: name,
          role: 'user',
          language: 'es',
        }, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });
      } else {
        await signInWithEmail(email, password);
      }
      navigate('/'); // Navigate to home after successful sign-in
    } catch (error: any) {
      captureException(error, { tags: { feature: 'login' }, extra: { action: 'emailSignIn', isSignUp } });
      console.error('Failed to sign in:', error);
      setError(parseErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    console.log('Signing in with Google');
    try {
      const user = await signInWithGoogle();
      console.log('User:', user);
      if (user && isSignUp) {
        const idToken = await user.user.getIdToken();
        await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/user`, {
          username: user.user.displayName,
          role: 'user',
          language: 'es',
        }, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });
      }
      navigate('/'); // Navigate to home after successful sign-in
    } catch (error: any) {
      captureException(error, { tags: { feature: 'login' }, extra: { action: 'googleSignIn' } });
      console.error('Failed to sign in with Google:', error);
      setError(parseErrorMessage(error));
    }
  };

  const handleMicrosoftSignIn = async () => {
    setError('');
    try {
      await signInWithMicrosoft();
      navigate('/'); // Navigate to home after successful sign-in
    } catch (error: any) {
      captureException(error, { tags: { feature: 'login' }, extra: { action: 'microsoftSignIn' } });
      console.error('Failed to sign in with Microsoft:', error);
      setError(parseErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container component="main" maxWidth="xs" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '90vh' }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.05)', }}>
        <Stack spacing={3} alignItems="center">
          <Box
            component="img"
            src="/logo.jpg"
            alt="PDI Futuro"
            sx={{
              width: '100%',
              maxWidth: 300,
              pb: 2,
            }}
          />
          {/* <Typography variant="subtitle1" color="text.secondary" sx={{ textAlign: 'center'}}>
            {isSignUp ? t('auth.signUp') : t('auth.signIn')}
          </Typography> */}
          
          {/* Email/Password Form */}
          <Box component="form" onSubmit={handleEmailSignIn} sx={{ width: '100%' }}>
            <Stack spacing={2}>
              {error && (
                <Alert severity="error" sx={{ borderRadius: '0px !important', fontSize: '0.875rem' }}>
                  {error}
                </Alert>
              )}
              
              {isSignUp && (
                <TextField
                  required
                  fullWidth
                  label={t('auth.name')}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              )}
              
              <TextField
                required
                fullWidth
                label={t('auth.email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              <TextField
                required
                fullWidth
                label={t('auth.password')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              
              {isSignUp && (
                <TextField
                  required
                  fullWidth
                  label={t('auth.confirmPassword')}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              )}
              
              <Button
                type="submit"
                variant="contained"
                size="large"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                sx={{ 
                  py: 1.5, 
                  textTransform: 'none', 
                  fontSize: '1.1rem', 
                  borderRadius: 2,
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  isSignUp ? t('auth.signUp') : t('auth.signIn')
                )}
              </Button>
            </Stack>
          </Box>

          {!isSignUp && (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 0 }}>
              <Link
                component="button"
                variant="body2"
                onClick={handleOpenResetDialog}
                sx={{ 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                {t('auth.forgotPassword')}
              </Link>
            </Box>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', marginTop: !isSignUp ? "6px !important" : "12px" }}>
            {isSignUp ? t('auth.alreadyHaveAccount') : t('auth.dontHaveAccount')}{' '}
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError('');
                setName('');
                setConfirmPassword('');
              }}
              sx={{ textDecoration: 'none' }}
            >
              {isSignUp ? t('auth.signIn') : t('auth.signUp')}
            </Link>
          </Typography>
          
          <Divider sx={{ width: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              {t('common.or')}
            </Typography>
          </Divider>
          
          <Button
            variant="contained"
            size="large"
            onClick={handleGoogleSignIn}
            fullWidth
            sx={{ 
              py: 1.5, 
              textTransform: 'none', 
              fontSize: '1.02rem', 
              borderRadius: 2,
              backgroundColor: '#ffffff',
              color: '#757575',
              border: '1px solid #dadce0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              '&:hover': {
                backgroundColor: '#f8f9fa',
                boxShadow: '0 2px 4px rgba(0,0,0,0.12)'
              }
            }}
            startIcon={
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{
                  width: 18,
                  height: 18,
                  mr: 1
                }}
              >
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </Box>
            }
          >
            {isSignUp ? t('auth.signUp') : t('auth.signIn')} {t('common.with')} Google
          </Button>
          
          {/* <Button
            variant="outlined"
            size="large"
            onClick={handleMicrosoftSignIn}
            fullWidth
            sx={{ 
              py: 1.5, 
              ml: 0.5,
              mr: 0.5,
              textTransform: 'none', 
              fontSize: '1.02rem', 
              borderRadius: 2,
              borderColor: '#0078d4',
              color: '#0078d4',
              '&:hover': {
                borderColor: '#106ebe',
                backgroundColor: 'rgba(0, 120, 212, 0.04)'
              }
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 23 23"
              sx={{
                width: 20,
                height: 20,
                mr: 1,
                fill: 'currentColor'
              }}
            >
              <path d="M0 0h11v11H0zM12 0h11v11H12zM0 12h11v11H0zM12 12h11v11H12z"/>
            </Box>
            {isSignUp ? t('auth.signUp') : t('auth.signIn')} {t('common.with')} Microsoft
          </Button> */}
        </Stack>
      </Paper>
      
      {/* Password Reset Dialog */}
      <Dialog 
        open={resetDialogOpen} 
        onClose={handleCloseResetDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('auth.resetPasswordTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('auth.resetPasswordDescription')}
            </Typography>
            
            {resetSuccess && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {t('auth.resetPasswordSuccess')}
              </Alert>
            )}
            
            {resetError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {resetError}
              </Alert>
            )}
            
            <TextField
              autoFocus
              fullWidth
              label={t('auth.email')}
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              variant="outlined"
              size="small"
              disabled={isResetting || resetSuccess}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleCloseResetDialog}
            disabled={isResetting}
            sx={{ textTransform: 'none' }}
          >
            {t('common.close')}
          </Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            disabled={isResetting || resetSuccess || !resetEmail}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {isResetting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('auth.resetPassword')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login; 